import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Field } from './Form';
import { assessPassword } from '../lib/password';
import type { PasswordPolicyDto } from '../lib/types';

/**
 * Password input with a show/hide toggle. `autoComplete` is required rather than optional so no
 * caller can forget to distinguish a new password from the current one.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  hint,
  error,
  required,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'new-password' | 'current-password';
  hint?: string;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}): JSX.Element {
  const [visible, setVisible] = useState(false);

  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(id, describedBy) => (
        <div className="password-row">
          <input
            id={id}
            className="input"
            type={visible ? 'text' : 'password'}
            value={value}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
            spellCheck={false}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm password-toggle"
            aria-pressed={visible}
            aria-controls={id}
            aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
            disabled={disabled}
            onClick={() => setVisible((current) => !current)}
          >
            {visible ? 'Hide' : 'Show'}
          </button>
        </div>
      )}
    </Field>
  );
}

/**
 * Live policy feedback: a strength meter plus the individual rules. Rendered under the new-password
 * field on signup, reset, invitation acceptance and change-password.
 */
export function PasswordStrength({
  password,
  policy,
}: {
  password: string;
  policy: PasswordPolicyDto;
}): JSX.Element {
  const assessment = assessPassword(password, policy);

  return (
    <div className="password-strength">
      <div className="password-strength-head">
        <span className="password-strength-label">Password strength</span>
        <span className={`password-strength-value password-strength-${assessment.tone}`}>
          {assessment.label}
        </span>
      </div>

      <div
        className={`progress progress-${assessment.tone === 'bad' ? 'bad' : assessment.tone === 'good' ? 'good' : 'info'}`}
        role="progressbar"
        aria-valuenow={assessment.score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Password strength"
      >
        <div className="progress-fill" style={{ width: `${assessment.score}%` }} />
      </div>

      <ul className="password-rules">
        {assessment.rules.map((rule) => (
          <li
            key={rule.key}
            className={`password-rule ${rule.met ? 'password-rule-met' : ''}`.trim()}
          >
            <span className="password-rule-mark" aria-hidden="true">
              {rule.met ? '✓' : '○'}
            </span>
            {rule.label}
            <span className="visually-hidden">{rule.met ? ' — met' : ' — not met yet'}</span>
          </li>
        ))}
      </ul>

      {policy.description ? <p className="field-hint">{policy.description}</p> : null}
    </div>
  );
}
